/**
 * Provider de auth sobre Supabase (ex FirebaseProvider de Firebase Auth).
 * Mantiene el nombre del archivo y los exports (useFirebase / FirebaseProvider)
 * para no tocar a los ~15 consumidores; `user` expone un shape compatible
 * ({ uid, email }) con lo que los componentes ya usan.
 *
 * El profile lo crea el trigger `handle_new_user` en Postgres SOLO si el email
 * está en finanzas.allowed_emails; si no existe → acceso denegado y signOut
 * (reemplaza la autocreación de perfiles que hacía la versión Firebase).
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getSupabase } from '@potencial/supabase';
import { UserProfile } from '../types';
import { setAuthUser, AuthUserInfo } from '../lib/authState';

interface FirebaseContextType {
  user: AuthUserInfo | null;
  profile: UserProfile | null;
  effectiveUserId: string | null;
  loading: boolean;
  isInitialized: boolean;
  authError: string | null;
}

const FirebaseContext = createContext<FirebaseContextType>({
  user: null,
  profile: null,
  effectiveUserId: null,
  loading: true,
  isInitialized: false,
  authError: null,
});

export const useFirebase = () => useContext(FirebaseContext);

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUserInfo | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const supabase = getSupabase();

    const loadProfile = async (uid: string) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', uid)
        .maybeSingle();

      if (error) {
        console.error('Error cargando profile:', error);
        setAuthError(`Error al cargar el perfil: ${error.message}`);
        setProfile(null);
        return;
      }
      if (!data) {
        // Email no autorizado en finanzas.allowed_emails → sin profile → afuera.
        await supabase.auth.signOut();
        setAuthError('Acceso denegado: este correo no está habilitado en Finanzas.');
        setUser(null);
        setAuthUser(null);
        setProfile(null);
        return;
      }
      setAuthError(null);
      setProfile({
        id: data.id,
        email: data.email,
        role: data.role,
        createdAt: data.createdAt,
        linkedTo: data.linkedTo ?? undefined,
      });
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // Nota: el callback NO debe ser async (deadlock conocido de supabase-js);
      // el trabajo real va en una microtask aparte.
      setTimeout(async () => {
        if (session?.user) {
          const info: AuthUserInfo = { uid: session.user.id, email: session.user.email ?? null };
          setUser(info);
          setAuthUser(info);
          await loadProfile(info.uid);
        } else {
          setUser(null);
          setAuthUser(null);
          setProfile(null);
        }
        setLoading(false);
        setIsInitialized(true);
      }, 0);
    });

    // Sesión inicial (si no hay ninguna persistida, cortar el loading).
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        setLoading(false);
        setIsInitialized(true);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const effectiveUserId = profile?.linkedTo || user?.uid || null;

  return (
    <FirebaseContext.Provider value={{ user, profile, effectiveUserId, loading, isInitialized, authError }}>
      {children}
    </FirebaseContext.Provider>
  );
};
